import * as openai from './openai';
import * as anthropic from './anthropic';

/**
 * Quick Request Service for single AI requests without conversation history
 * This service provides a stateless way to interact with AI providers
 */
export default class QuickRequestService {
  constructor() {
    this.provider = localStorage.getItem('paybyrd_ai_provider') || 'openai';
  }

  /**
   * Get current provider
   * @returns {string} Current provider name
   */
  getProvider() {
    return this.provider;
  }

  /**
   * Set AI provider
   * @param {string} provider - Provider name ('openai' or 'anthropic')
   */
  setProvider(provider) {
    if (provider !== 'openai' && provider !== 'anthropic') {
      throw new Error('Invalid provider');
    }
    this.provider = provider;
    localStorage.setItem('paybyrd_ai_provider', provider);
  }

  /**
   * Check if API key is set for the current provider
   * @returns {boolean} True if API key is set
   */
  hasApiKey() {
    return this.provider === 'openai'
      ? openai.hasOpenAIApiKey()
      : anthropic.hasAnthropicApiKey();
  }

  /**
   * Set API key for the current provider
   * @param {string} apiKey - API key
   */
  setApiKey(apiKey) {
    if (this.provider === 'openai') {
      openai.setOpenAIApiKey(apiKey);
    } else {
      anthropic.setAnthropicApiKey(apiKey);
    }
  }

  /**
   * Clear API key for the current provider
   */
  clearApiKey() {
    if (this.provider === 'openai') {
      openai.clearOpenAIApiKey();
    } else {
      anthropic.clearAnthropicApiKey();
    }
  }

  /**
   * Get the selected model for the current provider
   * @returns {string} Model name
   */
  getModel() {
    const modelStorageKeys = {
      openai: 'paybyrd_openai_model',
      anthropic: 'paybyrd_anthropic_model',
    };
    
    const defaultModels = {
      openai: 'gpt-4-turbo',
      anthropic: 'claude-3-5-sonnet-20241022',
    };
    
    const storageKey = modelStorageKeys[this.provider];
    return localStorage.getItem(storageKey) || defaultModels[this.provider];
  }

  /**
   * Set the model for the current provider
   * @param {string} model - Model name
   */
  setModel(model) {
    const modelStorageKeys = {
      openai: 'paybyrd_openai_model',
      anthropic: 'paybyrd_anthropic_model',
    };
    
    const storageKey = modelStorageKeys[this.provider];
    localStorage.setItem(storageKey, model);
  }

  /**
   * Send a single request to the AI service without conversation history
   * @param {string} message - User message
   * @param {string} accessToken - Auth token for API authorization
   * @param {Object} selectedStore - Selected store for context
   * @returns {Promise<string>} AI response
   */
  async sendSingleRequest(
    message,
    accessToken = null,
    selectedStore = null
  ) {
    if (!this.hasApiKey()) {
      throw new Error('ai_chat_api_key_missing');
    }

    try {
      if (this.provider === 'openai') {
        return await openai.sendMessageToOpenAI(
          message,
          [], // Empty array - no conversation history
          accessToken,
          selectedStore
        );
      } else {
        return await anthropic.sendMessageToAnthropic(
          message,
          [], // Empty array - no conversation history
          accessToken,
          selectedStore
        );
      }
    } catch (error) {
      console.error(
        `Error in QuickRequestService.sendSingleRequest with ${this.provider}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Send a message with conversation history
   * @param {string} message - User message
   * @param {Array} conversationHistory - Previous messages in the conversation
   * @param {string} accessToken - Auth token for API authorization
   * @param {Object} selectedStore - Selected store for context
   * @returns {Promise<string>} AI response
   */
  async sendMessageWithHistory(
    message,
    conversationHistory = [],
    accessToken = null,
    selectedStore = null
  ) {
    if (!this.hasApiKey()) {
      throw new Error('ai_chat_api_key_missing');
    }

    try {
      if (this.provider === 'openai') {
        return await openai.sendMessageToOpenAI(
          message,
          conversationHistory,
          accessToken,
          selectedStore
        );
      } else {
        return await anthropic.sendMessageToAnthropic(
          message,
          conversationHistory,
          accessToken,
          selectedStore
        );
      }
    } catch (error) {
      console.error(
        `Error in QuickRequestService.sendMessageWithHistory with ${this.provider}:`,
        error
      );
      throw error;
    }
  }
}